import { COMPLIANCE_COLORS } from './constants';
import { ComplianceStatusEnum } from '../../../shared/types/business-functions-workarounds';
import { GenericBadge } from '../../dataInputs/tiers/components';

type ComplianceBadgeProps = {
  status: ComplianceStatusEnum;
  label: string;
  noWorkaround?: boolean;
};

type ComplianceBadgeStyles = {
  color: string;
  backgroundColor: string;
  borderColor: string;
};

/**
 * Gets the appropriate styles for a compliance badge based on status and workaround flag
 * @param status The compliance status
 * @param noWorkaround Whether there is no workaround
 * @returns Object containing color, backgroundColor, and borderColor
 */
export const getComplianceBadgeStyles = (
  status: ComplianceStatusEnum,
  noWorkaround: boolean,
): ComplianceBadgeStyles => {
  if (noWorkaround || status === ComplianceStatusEnum.NOT_STARTED) {
    return {
      color: COMPLIANCE_COLORS.gray.text,
      backgroundColor: COMPLIANCE_COLORS.gray.background,
      borderColor: COMPLIANCE_COLORS.gray.border,
    };
  }

  switch (status) {
    case ComplianceStatusEnum.COMPLIANT:
      return {
        color: COMPLIANCE_COLORS.green.text,
        backgroundColor: COMPLIANCE_COLORS.green.background,
        borderColor: COMPLIANCE_COLORS.green.border,
      };
    case ComplianceStatusEnum.PARTIALLY_COMPLIANT:
      return {
        color: COMPLIANCE_COLORS.yellow.text,
        backgroundColor: COMPLIANCE_COLORS.yellow.background,
        borderColor: COMPLIANCE_COLORS.yellow.border,
      };
    case ComplianceStatusEnum.NON_COMPLIANT:
      return {
        color: COMPLIANCE_COLORS.red.text,
        backgroundColor: COMPLIANCE_COLORS.red.background,
        borderColor: COMPLIANCE_COLORS.red.border,
      };
    default:
      return {
        color: COMPLIANCE_COLORS.gray.text,
        backgroundColor: COMPLIANCE_COLORS.gray.background,
        borderColor: COMPLIANCE_COLORS.gray.border,
      };
  }
};

export const ComplianceBadge = ({
  status,
  label,
  noWorkaround = false,
}: ComplianceBadgeProps) => {
  const styles = getComplianceBadgeStyles(status, noWorkaround);

  return (
    <GenericBadge
      color={styles.backgroundColor}
      name={label}
      style={{
        color: styles.color,
        backgroundColor: styles.backgroundColor,
        border: `1px solid ${styles.borderColor}`,
      }}
    />
  );
};
